import { post, get } from "../http-client/client";
import { ApiResponse } from "../http-client/Response";
import { UserStatus } from "./User";

interface Tokens {
  accessToken: string;
  refreshToken: string;
}

export interface PendingUser {
  signupCode: string;
  signupExpirationTimestamp: Date;
  id: string;
  email: string;
  status: UserStatus;
}

export interface InvitationResponse {
  code: string;
  expiresAt: Date;
  inviteUrl: string;
}

export interface InvitationValidationResponse {
  valid: boolean;
  organizationName?: string;
  /** Address the invitation was sent to (the account is created for it) */
  email?: string;
  expired?: boolean;
  used?: boolean;
}

export interface CompleteInvitationData {
  firstName: string;
  lastName: string;
  /** Only for shared invitation links; emailed invitations use their own address */
  email?: string;
  password: string;
  company?: string;
  jobTitle?: string;
  legalHoursPerWeek?: number;
}

export const login = (email: string, password: string): Promise<ApiResponse<Tokens>> => {
  return post(
    "/auth/login",
    {
      email: email,
      password: password,
    },
  );
};

export const inviteUser = (email: string, organizationId?: string): Promise<ApiResponse<PendingUser>> => {
  return post(
    "/auth",
    {
      email,
      ...(organizationId && { organizationId }),
    },
  );
};

export const resendEmail = (userId: string): Promise<ApiResponse<PendingUser>> => {
  return post(
    `/auth/resend-email?userId=${userId}`,
  );
};

export const logout = (): Promise<ApiResponse<void>> => {
  return post("/auth/logout");
};

export const refreshTokens = (refreshToken: string): Promise<ApiResponse<Tokens>> => {
  return get("/auth/token", null, {
    headers: { Authorization: "Bearer " + refreshToken },
  });
};

export const register = async (signUpCode: string, password: string): Promise<ApiResponse<Tokens>> => {
  return post(
    "/auth/register",
    {
      signUpCode: signUpCode,
      password: password,
    },
  );
};

export const createInvitation = (organizationId?: string): Promise<ApiResponse<InvitationResponse>> => {
  return post(
    "/auth/invitation",
    {
      ...(organizationId && { organizationId }),
    },
  );
};

export const sendInvitationEmail = (
  email: string,
  organizationId?: string,
  hiddenFromMembers?: boolean,
): Promise<ApiResponse<InvitationResponse>> => {
  return post(
    "/auth/invitation/send-email",
    {
      email,
      ...(organizationId && { organizationId }),
      ...(hiddenFromMembers && { hiddenFromMembers: true }),
    },
  );
};

export const validateInvitation = (code: string): Promise<ApiResponse<InvitationValidationResponse>> => {
  return get(`/auth/invitation/${code}/validate`, null);
};

export const completeInvitation = (code: string, data: CompleteInvitationData): Promise<ApiResponse<Tokens>> => {
  return post(
    `/auth/invitation/${code}/register`,
    data,
  );
};

export const sendExistingInvitationEmail = (code: string, email: string): Promise<ApiResponse<void>> => {
  return post(
    `/auth/invitation/${code}/send-email`,
    { email },
  );
};

export const forgotPassword = (email: string): Promise<ApiResponse<{ success: boolean }>> => {
  return post(
    "/auth/forgot-password",
    { email },
  );
};

export const resetPassword = (token: string, password: string): Promise<ApiResponse<{ success: boolean }>> => {
  return post(
    "/auth/reset-password",
    { token, password },
  );
};
